/**
 * ══════════════════════════════════════════════════════════════════════════════
 * PTEL DESIGN SYSTEM v2.0 - SpinnerSnake
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Indicador de proceso del wizard: un arco que gira mientras se estira y se
 * encoge («serpiente»). Calco sin dependencia del `Snake` de loading-dev 0.3.4
 * (MIT): `<svg viewBox="0 0 24 24">` con un círculo de trazo 2,5 (nítido a
 * 16 px), un `<g>` que rota 360° en `duration` ms (lineal) y el círculo con
 * `stroke-dasharray` 1 → 45 → 45 / 100 y `stroke-dashoffset` 0 → −17 → −62
 * (ease-in-out, mismo periodo).
 *
 * Regla de coherencia (SERP1, 24-sep-2026): todo estado de proceso visible del
 * wizard lleva esta misma serpiente, 16 px y `colors.success`, a la derecha de
 * la cifra `n/total (pct%)` cuando la hay y junto al texto de estado cuando no.
 *
 * `prefers-reduced-motion: reduce` → sin animación y arco fijo (`18 100`).
 * El CSS se inyecta en `<head>` una sola vez por documento.
 *
 * Uso:
 *   <SpinnerSnake label="Geolocalizando infraestructuras" />
 *   <SpinnerSnake size={28} color={colors.primary} />
 *
 * ══════════════════════════════════════════════════════════════════════════════
 */

import { useInsertionEffect, type CSSProperties, type FC } from 'react';
import { colors } from '../design-tokens';

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────────────────────

export interface SpinnerSnakeProps {
  /** Lado del cuadrado en px (default: 16) */
  size?: number;
  /** Color del trazo, por token del DS (default: colors.success) */
  color?: string;
  /** Periodo de giro y de estiramiento en ms (default: 1400) */
  duration?: number;
  /**
   * Texto para lectores de pantalla (`role="status"` + `aria-label`).
   * Sin `label` el SVG es decorativo (`aria-hidden`): la cifra o el texto
   * de al lado ya cuentan el estado.
   */
  label?: string;
  className?: string;
  style?: CSSProperties;
}

// ─────────────────────────────────────────────────────────────────────────────
// CSS (una sola inyección por documento)
// ─────────────────────────────────────────────────────────────────────────────

export const SPINNER_SNAKE_STYLE_ID = 'ptel-spinner-snake';

const CSS = `
.ptel-snake { display: inline-block; flex: none; vertical-align: middle; }
.ptel-snake g { transform-origin: center; animation: ptel-snake-rot var(--ptel-snake-duration, 1400ms) linear infinite; }
.ptel-snake circle { animation: ptel-snake-stretch var(--ptel-snake-duration, 1400ms) ease-in-out infinite; }
@keyframes ptel-snake-rot { to { transform: rotate(360deg); } }
@keyframes ptel-snake-stretch {
  0% { stroke-dasharray: 1 100; stroke-dashoffset: 0; }
  50% { stroke-dasharray: 45 100; stroke-dashoffset: -17; }
  100% { stroke-dasharray: 45 100; stroke-dashoffset: -62; }
}
@media (prefers-reduced-motion: reduce) {
  .ptel-snake g, .ptel-snake circle { animation: none; }
  .ptel-snake circle { stroke-dasharray: 18 100; }
}
`;

function injectStyleOnce(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SPINNER_SNAKE_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = SPINNER_SNAKE_STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────────────────────

export const SpinnerSnake: FC<SpinnerSnakeProps> = ({
  size = 16,
  color = colors.success,
  duration = 1400,
  label,
  className,
  style,
}) => {
  useInsertionEffect(injectStyleOnce, []);

  const a11y = label
    ? { role: 'status' as const, 'aria-label': label }
    : { 'aria-hidden': true as const };

  return (
    <svg
      data-testid="spinner-snake"
      className={className ? `ptel-snake ${className}` : 'ptel-snake'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={{ color, ['--ptel-snake-duration' as string]: `${duration}ms`, ...style } as CSSProperties}
      {...a11y}
    >
      <g>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </g>
    </svg>
  );
};

export default SpinnerSnake;
